import { TRANSACTION_TYPE_LABELS } from '@/app/my-wallet/utils/constants';
import { formatCreditNaira, humanizeServiceType, transactionTypeMeta } from '@/app/my-wallet/utils/transactions';
import { type IWalletTransaction } from '@/store/api';

export type SlipTone = 'in' | 'out' | 'neutral';

export interface ITransactionPresentation {
	tone: SlipTone;
	/** Neutral-coloured caption above the amount. Direction is carried by the glyph and the sign, never by text colour. */
	kicker: string;
	sign: '+' | '−' | '';
	/** What the type row says. `recognised: false` renders it as a muted "not recognised" value. */
	typeLabel: string;
	recognised: boolean;
	documentTitle: string;
	downloadLabel: string;
}

const BY_KEY = {
	credit: { tone: 'in', kicker: 'Credits added', sign: '+', documentTitle: 'Receipt', downloadLabel: 'Download receipt' },
	debit: { tone: 'out', kicker: 'Credits spent', sign: '−', documentTitle: 'Receipt', downloadLabel: 'Download receipt' },
	unknown: {
		tone: 'neutral',
		kicker: 'Wallet entry',
		sign: '',
		documentTitle: 'Transaction record',
		downloadLabel: 'Download record',
	},
} as const;

const titleCase = (raw: string) => raw.charAt(0) + raw.slice(1).toLowerCase();

/**
 * How one transaction type is presented. Built on `transactionTypeMeta`, the table the list's
 * type pill reads, so the panel and the row it was opened from cannot disagree.
 *
 * My Wallet requests `includeInternalTransactions: false`, so only Credit and Debit are expected
 * here. Anything else still gets an honest neutral slip — named from the wire label when there is
 * one (`RESERVE` → "Reserve"), "Not recognised (n)" when there is not — never a credit/debit sign.
 */
export const transactionPresentation = (transactionType: number): ITransactionPresentation => {
	const meta = transactionTypeMeta(transactionType);
	const base = BY_KEY[meta.key];
	if (meta.key !== 'unknown') return { ...base, typeLabel: meta.label, recognised: true };

	const wireLabel = transactionType > 0 ? TRANSACTION_TYPE_LABELS[transactionType] : undefined;
	return wireLabel
		? { ...base, typeLabel: titleCase(wireLabel), recognised: true }
		: { ...base, typeLabel: `Not recognised (${transactionType})`, recognised: false };
};

/** The backend serializes unset fields as the literal string "null" — truthy, so it must be coerced first. */
export const cleanField = (value?: string | null): string | undefined => {
	const trimmed = value?.trim();
	if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return undefined;
	return trimmed;
};

export const formatCredits = (value: number) => Math.abs(value).toLocaleString('en-NG', { maximumFractionDigits: 2 });

export const formatSlipDate = (iso: string): string | undefined => {
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return undefined;
	return date.toLocaleString('en-GB', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

/**
 * The rate in hand is TODAY's, not the one in force when the transaction happened — so the
 * estimate says so rather than reading as what was paid.
 */
export const nairaAtTodaysRate = (credits: number, nairaPerCredit?: number): string | undefined => {
	const estimate = formatCreditNaira(Math.abs(credits), nairaPerCredit);
	return estimate ? `${estimate} at today's rate` : undefined;
};

export const slipTitle = (tx: IWalletTransaction): string => {
	const service = cleanField(tx.serviceType);
	return service ? humanizeServiceType(service) : transactionPresentation(tx.transactionType).documentTitle;
};

export const slipFileName = (tx: IWalletTransaction): string => {
	const { documentTitle } = transactionPresentation(tx.transactionType);
	const stem = (cleanField(tx.reference) ?? tx.id).replace(/[^A-Za-z0-9_-]+/g, '-').slice(0, 60);
	return `${documentTitle.toLowerCase().replace(/\s+/g, '-')}-${stem}.pdf`;
};
